import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useOfficialTerms } from "../lib/officialTerms";
import { JmLaunchPanel } from "../components/jm/JmLaunchPanel";
import { ConsentBar, ConsentCheck } from "../components/jm/ConsentTracker";
import { WeighInTab } from "../components/jm/WeighInTab";
import { RegistrationEditDialog } from "../components/jm/RegistrationEditDialog";
import { FIN_STYLE } from "../components/jm/RegistrationFinance";
import { BracketsGuardPanel } from "../components/jm/BracketsGuardPanel";
import { EventEditDialog } from "../components/jm/EventEditDialog";
import { SchoolsPanel } from "../components/jm/SchoolsPanel";
import { SupplementReminders } from "../components/jm/SupplementReminders";
import { JmRecherchePanel } from "../components/jm/JmRecherchePanel";
import { toast } from "sonner";
import { PosterPackButton } from "../components/PosterPackButton";
import { ParticipationsTab } from "../components/jm/ParticipationsTab";
import { PetitsSamouraisScores } from "../components/jm/PetitsSamouraisScores";
import { RulesTab, DocumentsTab } from "../components/jm/v2/RulesDocsTabs";
import { PetiteReleveTab } from "../components/jm/v2/PetiteReleveTab";
import { ControlCenterTab } from "../components/jm/v2/ControlCenterTab";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import CompetitionModule from "./CompetitionModule";
import { PUBLIC_HOST } from "../lib/domain";

const DISCIPLINE_LABEL = {
  muay_thai_educatif: "🥊 Kickboxing/Muaythai",
  kickboxing_educatif: "🥊 Kickboxing/Muaythai",
  jjb: "🥋 JJB",
  can_ryu_demo: "🇯🇵 Jiu-jitsu Can Ryu (démo)",
  petits_samourais: "🐣 Petits Samouraïs (parcours)",
};

const STATUS_LABEL = {
  draft: { label: "Brouillon", color: "border-zinc-700 text-zinc-400" },
  open: { label: "Inscriptions ouvertes", color: "border-emerald-500/40 text-emerald-300" },
  closed: { label: "Inscriptions fermées", color: "border-amber-500/40 text-amber-300" },
  suspended: { label: "Inscriptions suspendues (validation sécurité)", color: "border-red-500/40 text-red-300" },
  in_progress: { label: "En cours", color: "border-red-500/40 text-red-300" },
  completed: { label: "Terminée", color: "border-purple-500/40 text-purple-300" },
};

const LEVEL_STYLE = {
  remarquable: { label: "Performance remarquable", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  progression: { label: "Belle progression", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  engagee: { label: "Participation engagée", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
};
const CRITERIA = [
  ["technique", "Technique"], ["controle", "Contrôle"], ["esprit_sportif", "Esprit sportif"], ["engagement", "Engagement"],
];
const EMPTY_CRIT = () => ({ technique: 7, controle: 7, esprit_sportif: 8, engagement: 8 });

async function downloadPdf(url, filename, print = false) {
  const r = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  if (print) { window.open(href, "_blank", "noopener"); return; }
  const a = document.createElement("a"); a.href = href; a.download = filename; a.click();
  URL.revokeObjectURL(href);
}

export default function MiniCompetitionsModule() {
  const OT = useOfficialTerms();
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("inscriptions");
  const [details, setDetails] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [editReg, setEditReg] = useState(null);
  const [editEvent, setEditEvent] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [marketingKit, setMarketingKit] = useState(null);
  const [activeFormat, setActiveFormat] = useState("facebook_post");
  const [pageView, setPageView] = useState("events"); // events | planification
  const [posterEvent, setPosterEvent] = useState(null); // event object → affiche imprimable
  const [form, setForm] = useState({
    name: "", date: "", location: "", address: "", description: "",
    disciplines: ["jjb"], capacity_max: 120, fee_cad_per_discipline: 25,
    plateaux_schedule: "Muay Thaï — 10 h — Plateau A\nJiu-jitsu (JJB) — 13 h — Plateau B\nCan-Ryu / autodéfense — 15 h — Plateau C",
    waiver_text: "Le parent/tuteur accepte les risques inhérents aux arts martiaux compétitifs. La sécurité de mon enfant est ma responsabilité partagée avec les organisateurs et officiels certifiés.",
  });

  const openAutoGenerate = async () => {
    try {
      const r = await api.get("/mini-competitions/dates/suggest-next");
      setSuggestion(r.data);
      setAutoOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur suggestion");
    }
  };

  const confirmAutoGenerate = async () => {
    try {
      const r = await api.post("/mini-competitions/auto-generate", { after_iso: null });
      toast.success(`✅ ${r.data.competition.name} créée (brouillon)`);
      setAutoOpen(false);
      setSuggestion(null);
      await load();
      setSelectedId(r.data.competition.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const loadMarketingKit = async () => {
    try {
      const r = await api.get(`/mini-competitions/${selectedId}/marketing-kit`);
      setMarketingKit(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié 📋`);
  };

  const createSandbox = async () => {
    try { const { data } = await api.post("/jm/sandbox"); toast.success(data.created ? "🧪 Bac à sable créé (2 athlètes test, 3 participations)" : "Bac à sable déjà présent"); await load(); setSelectedId(data.event_id); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const deleteEvent = async (e, ev) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer « ${ev.name} » ? (suppression douce, réversible par l'équipe ; refusée s'il existe des inscriptions payées)`)) return;
    try {
      await api.delete(`/mini-competitions/${ev.id}`);
      toast.success("Compétition supprimée");
      if (selectedId === ev.id) setSelectedId(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Suppression refusée"); }
  };

  const load = async () => {
    try {
      const r = await api.get("/mini-competitions");
      setEvents(r.data || []);
      if (!selectedId && r.data?.length > 0) setSelectedId(r.data[0].id);
    } catch {
      toast.error("Erreur chargement compétitions");
    }
  };

  const loadDetails = async (id) => {
    if (!id) return;
    try {
      const [event, regs, cats, bracks, res] = await Promise.all([
        api.get(`/mini-competitions/${id}`),
        api.get(`/mini-competitions/${id}/registrations`),
        api.get(`/mini-competitions/${id}/categories`).catch(() => ({ data: [] })),
        api.get(`/mini-competitions/${id}/brackets`).catch(() => ({ data: [] })),
        api.get(`/mini-competitions/${id}/results`).catch(() => ({ data: { results: [] } })),
      ]);
      setDetails({ event: event.data, regs: regs.data, cats: cats.data, brackets: bracks.data, results: res.data, loadedAt: Date.now() });
    } catch {
      toast.error("Erreur chargement détails");
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadDetails(selectedId); }, [selectedId]);

  const createEvent = async () => {
    if (!form.name || !form.date || form.disciplines.length === 0) {
      toast.error("Nom, date et au moins 1 discipline requis");
      return;
    }
    try {
      const r = await api.post("/mini-competitions", form);
      toast.success(`✅ Compétition créée : ${r.data.competition.name}`);
      setCreateOpen(false);
      setForm({ ...form, name: "", date: "", location: "" });
      await load();
      setSelectedId(r.data.competition.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur création");
    }
  };

  const updateStatus = async (status) => {
    try {
      await api.patch(`/mini-competitions/${selectedId}`, { status });
      toast.success(`Statut → ${STATUS_LABEL[status]?.label || status}`);
      await loadDetails(selectedId);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur update");
    }
  };

  const generateCategories = async () => {
    try {
      const r = await api.post(`/mini-competitions/${selectedId}/generate-categories`);
      toast.success(`🎯 ${r.data.categories_count} catégories générées`);
      await loadDetails(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const generateBrackets = async () => {
    try {
      const r = await api.post(`/mini-competitions/${selectedId}/generate-brackets`);
      toast.success(`🏆 ${r.data.brackets_created} brackets créés`);
      await loadDetails(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const computeResults = async () => {
    try {
      const r = await api.post(`/mini-competitions/${selectedId}/compute-results`);
      toast.success(`📊 ${r.data.athletes_scored} pointage(s) calculé(s) · ${r.data.archived_in_student_profiles} carnets mis à jour · ✉️ envoi des certificats aux parents en cours`);
      await loadDetails(selectedId);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const event = details.event;
  const publicUrl = event ? `${window.location.origin}/competitions/${event.slug}` : "";

  return (
    <div className="space-y-6" data-testid="mini-competitions-module">
      {editEvent && event && <EventEditDialog eventId={event.id} open={editEvent} onClose={() => setEditEvent(false)} onSaved={() => { loadDetails(event.id); load(); }} />}
      {editReg && event && <RegistrationEditDialog eventId={event.id} reg={editReg} open={!!editReg} onClose={() => setEditReg(null)} onSaved={() => loadDetails(event.id)} />}
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black">
        <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2">RÉSEAU KAIZEN · JEUX MARTIAUX INTER-ÉCOLES</div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid="jm-official-title">{OT.event_name}</h1>
        <p className="text-zinc-500 text-xs mt-1 text-amber-200/80">{OT.event_signature} · {OT.event_location} · {OT.event_date_label}</p>
        <p className="text-zinc-500 text-sm mt-2">Compétition structurée de développement athlétique — {OT.event_date_label}. Inscriptions multi-écoles, catégorisation, brackets, pointage par discipline, certificats de performance, affiches &amp; planification.</p>
        <Button
          onClick={() => setCreateOpen(true)}
          className="mt-4 mr-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
          data-testid="create-competition-btn"
        >
          + Créer une compétition
        </Button>
        <Button onClick={() => (window.location.href = "/app/jm-pointage")} variant="outline" className="mt-4 mr-2 border-zinc-700 text-zinc-200" data-testid="open-scoring-pilot-btn">🎛 Pilote pointage BJJ (table de marque)</Button>
        <Button
          onClick={openAutoGenerate}
          variant="outline"
          className="mt-4 border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
          data-testid="auto-generate-btn"
        >
          🪄 Auto-générer prochaine date (Abitibi-aware)
        </Button>
        <Button variant="outline" onClick={createSandbox} className="mt-4 border-zinc-600 text-zinc-300 hover:bg-zinc-800" data-testid="jm-sandbox-btn" title="Événement de test non public pour essayer le portail (participations, statuts, approbations)">
          🧪 Bac à sable (test portail)
        </Button>
      </div>

      {/* Bascule de vue : Événements / Planification */}
      <div className="flex flex-wrap gap-2" data-testid="jm-view-tabs">
        <button onClick={() => setPageView("events")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${pageView === "events" ? "bg-amber-500 text-black" : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`} data-testid="jm-tab-events">🥋 Événements &amp; inscriptions</button>
        <button onClick={() => setPageView("planification")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${pageView === "planification" ? "bg-amber-500 text-black" : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`} data-testid="jm-tab-planif">🗓️ Planification (conflits &amp; couverture)</button>
      </div>

      {/* Concept Jeux Martiaux */}
      {pageView === "events" && (
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.06] p-4" data-testid="jm-concept">
          <div className="text-sky-300 font-bold text-sm mb-2">🥋 {OT.event_name} — {OT.event_date_label}</div>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-zinc-300">
            <li>🥋 <b>Multidisciplinaire</b> — {Object.values(OT.disciplines || {}).join(" · ")}, plusieurs plateaux.</li>
            <li>🛡️ <b>Sécurité et pointage</b> — {OT.event_tagline} Victoire par pointage, décision ou arrêt de sécurité — jamais par la recherche du knockout. <a href="/reglement-securite" target="_blank" rel="noreferrer" className="underline text-emerald-300">Règlement</a></li>
            <li>🤝 <b>Inter-écoles</b> — le groupe fait progresser tes athlètes à fort potentiel.</li>
            <li>🖼️ <b>Affiche par événement</b> — génère un poster QR à afficher au dojo (bouton ci-dessous).</li>
          </ul>
        </div>
      )}

      {pageView === "planification" ? (
        <div className="border border-zinc-800 rounded-2xl p-4 md:p-6 bg-zinc-950/40" data-testid="jm-planification"><CompetitionModule embedded /></div>
      ) : (
      <div className="grid lg:grid-cols-[280px_minmax(0,1fr)] gap-6">
        {/* Sidebar events */}
        <div className="space-y-2" data-testid="events-list">
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Événements ({events.length})</div>
          {events.length === 0 && <div className="text-xs text-zinc-600 italic">Aucune compétition. Crée la première.</div>}
          {events.map((e) => {
            const st = STATUS_LABEL[e.status] || STATUS_LABEL.draft;
            return (
              <button
                key={e.id}
                onClick={() => setSelectedId(e.id)}
                className={`w-full text-left border rounded-xl p-3 transition ${selectedId === e.id ? "border-amber-500/60 bg-amber-500/5" : "border-zinc-800 hover:border-zinc-700 bg-zinc-950/40"}`}
                data-testid={`event-card-${e.id}`}
              >
                <div className="text-sm font-medium text-zinc-100 truncate">{e.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{e.date}{e.location ? ` · ${e.location}` : ""}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className={`${st.color} text-[10px]`}>{st.label}</Badge>
                  <span className="text-[10px] text-zinc-500">{e.registrations_count || 0} inscrits</span>
                  <span role="button" tabIndex={0} onClick={(ev) => deleteEvent(ev, e)} onKeyDown={(ev) => ev.key === "Enter" && deleteEvent(ev, e)}
                    className="ml-auto text-[10px] text-zinc-500 hover:text-red-400 px-1.5 py-0.5 rounded border border-zinc-800 hover:border-red-500/50" title="Supprimer" data-testid={`event-delete-${e.id}`}>🗑️</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail panel */}
        <div className="space-y-4 min-w-0">
          {!event ? (
            <div className="border border-zinc-800 rounded-2xl p-10 text-center text-zinc-500 text-sm">
              Sélectionne une compétition (ou crée-en une).
            </div>
          ) : (
            <>
              <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-2xl font-bold">{event.name}</h2>
                    <div className="text-sm text-zinc-500 mt-1">{event.date}{event.date_end && event.date_end !== event.date ? ` → ${event.date_end}` : ""}{event.location && ` · ${event.location}`} <button onClick={() => setEditEvent(true)} className="ml-2 rounded-lg border border-amber-500/50 text-amber-300 px-2 py-0.5 text-[11px] hover:bg-amber-500/10" data-testid="event-edit-btn">Modifier l'événement</button></div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {event.disciplines.map((d) => (
                        <Badge key={d} variant="outline" className="border-zinc-700 text-xs">{DISCIPLINE_LABEL[d] || d}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {event.status === "draft" && (
                      <Button onClick={() => updateStatus("open")} size="sm" className="bg-emerald-600 hover:bg-emerald-500" data-testid="open-registrations-btn">
                        Ouvrir inscriptions
                      </Button>
                    )}
                    {event.status === "open" && (
                      <Button onClick={() => updateStatus("closed")} size="sm" variant="outline">Fermer inscriptions</Button>
                    )}
                    {(event.status === "open" || event.status === "closed") && (
                      <Button onClick={() => window.confirm("Suspendre préventivement : la page publique affiche le message officiel de suspension, aucune inscription ni paiement possible. Les données existantes sont conservées.") && updateStatus("suspended")} size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="suspend-registrations-btn">⏸️ Suspendre (validation sécurité)</Button>
                    )}
                    {event.status === "suspended" && (
                      <span className="text-[11px] text-red-300 self-center" data-testid="suspended-label">⏸️ {OT.suspension_status_label} — réouverture uniquement par le Lancement contrôlé (Phase G) après les 8 validations.</span>
                    )}
                    {event.status === "closed" && (
                      <Button onClick={() => updateStatus("in_progress")} size="sm" className="bg-red-600 hover:bg-red-500">Démarrer compétition</Button>
                    )}
                    <Button onClick={() => setPosterEvent(event)} size="sm" variant="outline" className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10" data-testid="generate-poster-btn">🖼️ Affiche</Button>
                    {event.slug && <Button onClick={() => window.open(`/jeux-martiaux/live/${event.slug}`, "_blank", "noopener")} size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="live-screen-btn">📺 Écran live (points)</Button>}
                  </div>
                </div>
                <div className="mt-4"><JmLaunchPanel event={event} onLaunched={() => { load(); loadDetails(event.id); }} /></div>
                {(event.status === "open" || event.status === "closed") && (
                  <div className="mt-4 pt-4 border-t border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">URL PUBLIQUE D'INSCRIPTION · <a href={`${process.env.REACT_APP_BACKEND_URL}/api/mini-competitions/public/${event.slug}/consentement.pdf`} target="_blank" rel="noreferrer" className="text-emerald-300 hover:underline" data-testid="consent-pdf-link">📄 Consentement parental + déclaration médicale (PDF)</a></div>
                      <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 hover:underline break-all" data-testid="public-url-link">
                        {publicUrl}
                      </a>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Lien copié 📋"); }}
                    >📋 Copier</Button>
                  </div>
                )}
                {(event.status === "open" || event.is_test) && (
                  <div className="mt-3 flex items-center justify-between gap-3 flex-wrap rounded-lg border border-sky-500/30 bg-sky-500/5 px-3 py-2" data-testid="v2-url-block">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] text-sky-400 mb-1">INSCRIPTION GUIDÉE 11 ÉTAPES (v2 · brouillons · règlements · documents)</div>
                      <a href={`${publicUrl}/inscription`} target="_blank" rel="noreferrer" className="text-xs text-sky-300 hover:underline break-all" data-testid="v2-url-link">{publicUrl}/inscription</a>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${publicUrl}/inscription`); toast.success("Lien v2 copié 📋"); }} data-testid="v2-url-copy">📋 Copier</Button>
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-2 flex-wrap border-b border-zinc-900" data-testid="detail-tabs">
                {["inscriptions", "participations", "reglements", "documents", "categories", "pesee", "brackets", "resultats", "petite_releve", "controle", "marketing"].map((t) => (
                  <button
                    key={t}
                    onClick={() => { setTab(t); if (t === "marketing") loadMarketingKit(); }}
                    className={`px-4 py-2 text-sm capitalize transition ${tab === t ? "text-amber-400 border-b-2 border-amber-400" : "text-zinc-500 hover:text-zinc-300"}`}
                    data-testid={`tab-${t}`}
                  >
                    {t === "marketing" ? "🎤 Marketing Kit" : t === "petite_releve" ? "🐣 Petite relève" : t === "controle" ? "🎛️ Centre de contrôle" : t === "participations" ? "🥋 Participations" : t === "reglements" ? "📜 Règlements" : t === "documents" ? "🔒 Documents" : t === "pesee" ? "⚖️ Pesée" : t}
                  </button>
                ))}
              </div>

              {/* TAB Inscriptions */}
              {tab === "inscriptions" && (
                <div className="space-y-3" data-testid="tab-content-inscriptions">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI label="Total" value={details.regs?.total || 0} />
                    <KPI label="Écoles" value={Object.keys(details.regs?.by_school || {}).length} />
                    <KPI label="Catégories" value={details.cats?.length || 0} />
                    <KPI label="Certificats" value={details.results?.total || 0} accent />
                  </div>
                  <ConsentBar eventId={event.id} refreshKey={details.regs?.registrations?.length} />
                  {details.regs?.by_school && (
                    <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
                      <div className="text-[10px] tracking-[0.3em] text-zinc-500 mb-2">PAR ÉCOLE</div>
                      <div className="flex gap-2 flex-wrap">
                        {Object.entries(details.regs.by_school).map(([s, n]) => (
                          <Badge key={s} variant="outline" className="border-zinc-700 text-zinc-200">{s} ({n})</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <SupplementReminders eventId={event.id} reloadKey={details.loadedAt} />
                  <SchoolsPanel onChanged={() => loadDetails(event.id)} />
                  <div className="border border-zinc-800 rounded-xl overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-950/50 text-zinc-400 text-[10px] uppercase tracking-wider">
                        <tr>
                          <th className="px-3 py-2 text-left">Athlète</th>
                          <th className="px-3 py-2 text-left">Âge</th>
                          <th className="px-3 py-2 text-left">Poids</th>
                          <th className="px-3 py-2 text-left">École</th>
                          <th className="px-3 py-2 text-left">Disciplines</th>
                          <th className="px-3 py-2 text-left">Classe · format (MTO / FQJJB)</th>
                          <th className="px-3 py-2 text-left">Consentement</th>
                          <th className="px-3 py-2 text-left">Paiement</th>
                          <th className="px-3 py-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-900">
                        {(details.regs?.registrations || []).map((r) => (
                          <tr key={r.id} className="hover:bg-zinc-950/40">
                            <td className="px-3 py-2 text-zinc-200">{r.athlete_full_name}</td>
                            <td className="px-3 py-2 text-zinc-400">{r.athlete_age_at_event}</td>
                            <td className="px-3 py-2 text-zinc-400">{r.athlete_weight_kg ? `${r.athlete_weight_kg}kg` : "—"}</td>
                            <td className="px-3 py-2 text-zinc-400">{r.school_name}</td>
                            <td className="px-3 py-2 text-zinc-400">{(r.entries || []).map((e) => e.discipline.split("_")[0]).join(", ")}</td>
                            <td className="px-3 py-2 text-zinc-300 text-xs" data-testid={`reg-classes-${r.id}`}>
                              {(r.entries_classified || []).map((e, i) => (
                                <div key={i} className="flex items-center gap-1.5 whitespace-nowrap">
                                  <span className="text-zinc-500">{(e.discipline || "").split("_")[0]}</span>
                                  <b className="text-amber-300" title={e.cap_message || ""}>{e.fight_class}</b>
                                  {e.capped && <span className="text-[10px] text-red-300 whitespace-pre-line" data-testid={`reg-cap-${r.id}-${e.discipline}`}>{e.cap_message}</span>}
                                  <span className="text-zinc-500">· {e.round_format}</span>
                                  {!["jjb", "can_ryu_demo", "petits_samourais"].includes(e.discipline) && (
                                    <input type="number" min={0} defaultValue={e.fights || 0} title="Nombre de combats" data-testid={`reg-fights-${r.id}-${e.discipline}`}
                                      onBlur={async (ev) => { const v = Number(ev.target.value || 0); if (v === (e.fights || 0)) return; try { await api.patch(`/mini-competitions/${event.id}/registrations/${r.id}/fights`, { discipline: e.discipline, fights: v }); toast.success("Classe recalculée"); loadDetails(event.id); } catch { toast.error("Erreur"); } }}
                                      className="w-12 bg-zinc-950 border border-zinc-800 rounded px-1 py-0.5 text-[11px] text-zinc-200" />
                                  )}
                                </div>
                              ))}
                            </td>
                            <td className="px-3 py-2"><ConsentCheck eventId={event.id} reg={r} onChanged={() => loadDetails(event.id)} /></td>
                            <td className="px-3 py-2">
                              <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-200">{r.payment_status}</Badge>
                              {r.finance && r.finance.code !== "paiement_en_attente" && <div className={`mt-1 inline-block whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[10px] ${FIN_STYLE[r.finance.code] || ""}`} data-testid={`reg-fin-${r.id}`}>{r.finance.label}{r.finance.solde_cad ? ` · ${Math.abs(r.finance.solde_cad).toFixed(2)} $` : ""}</div>}
                              {r.status === "cancelled" && <div className="mt-1 inline-block rounded-full border border-red-500/40 text-red-300 px-1.5 py-0.5 text-[10px]" data-testid={`reg-cancelled-${r.id}`}>Inscription annulée</div>}
                            </td>
                            <td className="px-3 py-2">
                              <button onClick={() => setEditReg(r)} className="whitespace-nowrap rounded-lg border border-amber-500/50 text-amber-300 px-2 py-1 text-[11px] hover:bg-amber-500/10" data-testid={`reg-edit-${r.id}`}>Modifier l'inscription</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TAB Participations (Sprint 1 portail) */}
              {tab === "participations" && <ParticipationsTab eventId={selectedId} />}
              {tab === "reglements" && <div className="space-y-6"><JmRecherchePanel eventId={selectedId} event={event} /><RulesTab eventId={selectedId} /></div>}
              {tab === "documents" && <DocumentsTab eventId={selectedId} />}
              {tab === "pesee" && <WeighInTab eventId={selectedId} />}

              {/* TAB Categories */}
              {tab === "categories" && (
                <div className="space-y-3" data-testid="tab-content-categories">
                  <BracketsGuardPanel eventId={event.id} reloadKey={details.loadedAt} onChanged={() => loadDetails(selectedId)} />
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-zinc-400">{details.cats?.length || 0} catégories auto-générées</div>
                    <Button onClick={generateCategories} size="sm" className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="gen-cats-btn">
                      🎯 (Re)Générer catégories
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {(details.cats || []).map((c) => (
                      <div key={c.id} className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
                        <div className="text-sm font-semibold text-zinc-100">{c.label}</div>
                        <div className="text-xs text-zinc-500 mt-1">{c.athletes_count} athlètes · {c.match_duration_min} min/match</div>
                        <div className="mt-2 text-[10px] text-zinc-600">
                          {(c.athletes || []).map((a) => a.athlete_full_name).join(" · ")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB Brackets */}
              {tab === "brackets" && (
                <div className="space-y-3" data-testid="tab-content-brackets">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-zinc-400">{details.brackets?.length || 0} brackets · élimination simple · chaque fiche de match affiche classe, format de rounds et frappes à la tête retenus (le plus restrictif des deux athlètes)</div>
                    <div className="flex gap-2">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/mini-competitions/${event.id}/referee-card.pdf`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-md text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100" data-testid="referee-card-link">🖨️ Cartes arbitres par plateau (PDF)</a>
                      {(event.disciplines || []).includes("petits_samourais") && (
                        <button onClick={() => downloadPdf(`/mini-competitions/${event.id}/petits-samourais-feuille.pdf`, "", true).catch(() => toast.error("PDF impossible"))} className="px-3 py-1.5 rounded-md text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100" data-testid="ps-sheet-link">🐣 Feuille de parcours Petits Samouraïs (PDF juges)</button>
                      )}
                      <Button onClick={generateBrackets} size="sm" className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="gen-brackets-btn">
                        🏆 (Re)Générer brackets
                      </Button>
                    </div>
                  </div>
                  {(details.brackets || []).map((b) => (
                    <BracketView key={b.id} bracket={b} onRefresh={() => loadDetails(selectedId)} />
                  ))}
                </div>
              )}

              {/* TAB Resultats — pointage par discipline (aucune médaille) */}
              {tab === "resultats" && (
                <div className="space-y-4" data-testid="tab-content-resultats">
                  {(event.disciplines || []).includes("petits_samourais") && <PetitsSamouraisScores cid={selectedId} />}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm text-zinc-400">
                      {details.results?.total || 0} pointage(s) · <span className="text-zinc-500">victoire 3 pts · nul 2 pts · combat 1 pt + critères /10</span>
                      {(details.results?.total || 0) > 0 && <span className="ml-2 text-emerald-400" data-testid="handed-count">· 🤝 remis {(details.results?.results || []).filter((r) => r.handed_at).length}/{details.results.total}</span>}
                    </div>
                    <div className="flex gap-2">
                      {(details.results?.total || 0) > 0 && (
                        <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid="all-certificates-btn"
                          onClick={() => downloadPdf(`/mini-competitions/${selectedId}/certificates.pdf`, "certificats-jeux-martiaux.pdf").catch(() => toast.error("Téléchargement impossible"))}>
                          📜 Tous les certificats (PDF)
                        </Button>
                      )}
                      {(details.results?.total || 0) > 0 && (
                        <>
                          <Button size="sm" variant="outline" className="border-zinc-600 text-zinc-200 hover:bg-zinc-800" data-testid="print-certificates-btn"
                            onClick={() => downloadPdf(`/mini-competitions/${selectedId}/certificates.pdf`, "", true).catch(() => toast.error("Impression impossible"))}>
                            🖨️ Imprimer (remise au dojo)
                          </Button>
                          <Button size="sm" variant="outline" className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10" data-testid="send-certificates-btn"
                            onClick={async () => { try { const r = await api.post(`/mini-competitions/${selectedId}/certificates/send`); toast.success(`✉️ ${r.data.sent} certificat(s) envoyé(s) aux parents · ${r.data.skipped} sans courriel · ${r.data.failed} échec(s)`); await loadDetails(selectedId); } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); } }}>
                            ✉️ Envoyer aux parents
                          </Button>
                        </>
                      )}
                      <Button onClick={computeResults} size="sm" className="bg-purple-500 hover:bg-purple-400 text-white" data-testid="compute-results-btn">
                        📊 Calculer les pointages + archiver carnets
                      </Button>
                    </div>
                  </div>
                  {(details.results?.results || []).length === 0 ? (
                    <div className="border border-zinc-800 rounded-xl p-8 text-center text-sm text-zinc-500 italic" data-testid="results-empty">Aucun pointage — termine les combats puis clique « Calculer les pointages ».</div>
                  ) : (
                    [...new Set((details.results?.results || []).map((r) => r.discipline))].map((d) => (
                      <div key={d} className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/40" data-testid={`results-discipline-${d}`}>
                        <div className="px-4 py-2 bg-zinc-900/70 text-sm font-semibold text-amber-300">{DISCIPLINE_LABEL[d] || d}</div>
                        <table className="w-full text-xs">
                          <thead className="text-zinc-500 text-[10px] uppercase tracking-wider">
                            <tr>
                              <th className="text-left px-4 py-2">Athlète</th>
                              <th className="text-left px-2 py-2">Catégorie</th>
                              <th className="text-center px-2 py-2">Combats (V·N·D)</th>
                              <th className="text-center px-2 py-2">Pts combats</th>
                              <th className="text-center px-2 py-2">Critères /10</th>
                              <th className="text-center px-2 py-2">Total</th>
                              <th className="text-left px-2 py-2">Niveau</th>
                              <th className="text-left px-2 py-2">Remis au dojo</th>
                              <th className="text-right px-4 py-2">Certificat</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(details.results?.results || []).filter((r) => r.discipline === d).map((r) => {
                              const lv = LEVEL_STYLE[r.level] || LEVEL_STYLE.engagee;
                              return (
                                <tr key={r.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`result-row-${r.id}`}>
                                  <td className="px-4 py-2 text-zinc-100">{r.athlete_full_name}<div className="text-[10px] text-zinc-500">{r.school_name}</div></td>
                                  <td className="px-2 py-2 text-zinc-400">{r.category_label}</td>
                                  <td className="px-2 py-2 text-center text-zinc-300">{r.combats} <span className="text-zinc-500">({r.victoires}·{r.nuls}·{r.defaites})</span></td>
                                  <td className="px-2 py-2 text-center font-mono text-zinc-200">{r.combat_points}</td>
                                  <td className="px-2 py-2 text-center font-mono text-zinc-200" title={Object.entries(r.criteria_detail || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}>{r.criteria_avg ?? "—"}</td>
                                  <td className="px-2 py-2 text-center font-mono font-bold text-amber-300" data-testid={`result-total-${r.id}`}>{r.total_points}</td>
                                  <td className="px-2 py-2"><span className={`inline-block border rounded-full px-2 py-0.5 text-[10px] ${lv.cls}`}>{lv.label}</span></td>
                                  <td className="px-2 py-2 whitespace-nowrap">
                                    <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px]" title={r.handed_at ? `Remis le ${r.handed_at.slice(0, 10)} par ${r.handed_by || ""}${r.handed_to ? " à " + r.handed_to : ""}` : "Cocher quand le certificat imprimé est remis en personne"}>
                                      <input type="checkbox" checked={!!r.handed_at} data-testid={`handed-${r.id}`} className="accent-emerald-500"
                                        onChange={async (e) => {
                                          const handed = e.target.checked;
                                          const handed_to = handed ? (window.prompt("Remis à qui ? (parent, athlète, nom…)", "parent") ?? "") : null;
                                          try { await api.patch(`/mini-competitions/${selectedId}/results/${r.id}/handed`, { handed, handed_to }); await loadDetails(selectedId); toast.success(handed ? "🤝 Certificat remis en personne" : "Remise annulée"); }
                                          catch { toast.error("Erreur"); }
                                        }} />
                                      {r.handed_at ? <span className="text-emerald-400">🤝 {r.handed_at.slice(0, 10)}</span> : <span className="text-zinc-500">à remettre</span>}
                                    </label>
                                  </td>
                                  <td className="px-4 py-2 text-right whitespace-nowrap">
                                    {r.certificate_sent_at && <span className="text-[10px] text-emerald-400 mr-2" title={`Envoyé à ${r.certificate_email || ""}`} data-testid={`cert-sent-${r.id}`}>✉️ envoyé</span>}
                                    <button onClick={() => downloadPdf(`/mini-competitions/${selectedId}/certificates.pdf?rid=${r.id}`, `certificat-${r.athlete_full_name}.pdf`).catch(() => toast.error("Téléchargement impossible"))}
                                      className="text-[11px] px-2 py-1 rounded border border-zinc-700 hover:border-amber-500/50 hover:text-amber-300 text-zinc-300" title={r.recommendation} data-testid={`certificate-btn-${r.id}`}>📜 PDF</button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* TAB Marketing */}
              {tab === "petite_releve" && <PetiteReleveTab event={event} />}
              {tab === "controle" && <ControlCenterTab event={event} />}
              {tab === "marketing" && (
                <div className="space-y-3" data-testid="tab-content-marketing">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-zinc-400">Kit communication post-event (FB · IG · TikTok · SMS · Email · Affiche)</div>
                    <Button onClick={loadMarketingKit} size="sm" variant="outline" data-testid="reload-marketing-btn">🔄 Recharger</Button>
                  </div>
                  {!marketingKit ? (
                    <div className="border border-zinc-800 rounded-xl p-10 text-center text-sm text-zinc-500">Chargement du kit…</div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KPI label="Athlètes" value={marketingKit.stats.total_athletes} />
                        <KPI label="Écoles" value={marketingKit.stats.schools_count} />
                        <KPI label="Certificats" value={marketingKit.stats.certificates_awarded} />
                        <KPI label="Perf. remarquables" value={marketingKit.stats.levels?.remarquable ?? 0} accent />
                      </div>
                      <div className="flex gap-2 flex-wrap" data-testid="marketing-format-tabs">
                        {Object.entries(marketingKit.formats).map(([k, f]) => (
                          <button
                            key={k}
                            onClick={() => setActiveFormat(k)}
                            className={`px-3 py-1.5 rounded-full text-xs border transition ${activeFormat === k ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-zinc-700 text-zinc-400 hover:border-zinc-600"}`}
                            data-testid={`format-tab-${k}`}
                          >
                            {f.label} <span className="opacity-50">({f.chars})</span>
                          </button>
                        ))}
                      </div>
                      {marketingKit.formats[activeFormat] && (
                        <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40 space-y-3">
                          {marketingKit.formats[activeFormat].subject && (
                            <div>
                              <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">SUJET</div>
                              <div className="text-sm text-zinc-200 font-medium">{marketingKit.formats[activeFormat].subject}</div>
                            </div>
                          )}
                          {marketingKit.formats[activeFormat].script && (
                            <div>
                              <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">SCRIPT VIDÉO</div>
                              <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-sans bg-black/30 p-3 rounded">{marketingKit.formats[activeFormat].script}</pre>
                              <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1 mt-3">CAPTION</div>
                              <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-sans bg-black/30 p-3 rounded">{marketingKit.formats[activeFormat].caption}</pre>
                            </div>
                          )}
                          {marketingKit.formats[activeFormat].body && (
                            <div>
                              <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">CONTENU</div>
                              <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-sans bg-black/30 p-3 rounded max-h-96 overflow-y-auto" data-testid={`format-body-${activeFormat}`}>{marketingKit.formats[activeFormat].body}</pre>
                            </div>
                          )}
                          <Button
                            size="sm"
                            onClick={() => copyToClipboard(marketingKit.formats[activeFormat].body || marketingKit.formats[activeFormat].script || "", marketingKit.formats[activeFormat].label)}
                            className="bg-amber-500 hover:bg-amber-400 text-black"
                            data-testid="copy-format-btn"
                          >📋 Copier ce format</Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

            </>
          )}
        </div>
      </div>
      )}

      {/* Auto-generate Dialog */}
      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="auto-gen-dialog">
          <DialogHeader><DialogTitle>🪄 Date proposée par KAIZEN</DialogTitle></DialogHeader>
          {suggestion ? (
            <div className="space-y-4">
              <div className="border border-amber-500/40 bg-amber-500/5 rounded-xl p-4">
                <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">PROCHAINE COMPÉTITION OPTIMALE</div>
                <div className="text-3xl font-bold text-amber-300" data-testid="suggested-date">{suggestion.date}</div>
                <div className="text-sm text-zinc-400 mt-1">{suggestion.season} {suggestion.year} · Samedi</div>
              </div>
              {suggestion.conflicts_avoided?.length > 0 && (
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-zinc-500 mb-2">✅ ÉVÉNEMENTS RÉGIONAUX ÉVITÉS</div>
                  <div className="space-y-1">
                    {suggestion.conflicts_avoided.map((co, i) => (
                      <div key={i} className="text-xs text-zinc-400">• {co.label}</div>
                    ))}
                  </div>
                </div>
              )}
              {suggestion.alternatives?.length > 0 && (
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-zinc-500 mb-2">DATES ALTERNATIVES</div>
                  <div className="flex gap-2 flex-wrap">
                    {suggestion.alternatives.map((a, i) => (
                      <Badge key={i} variant="outline" className="border-zinc-700 text-zinc-400">{a.date}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-[11px] italic text-zinc-500">
                Cette date crée un événement <strong>brouillon</strong>. Tu peux ensuite modifier, ouvrir les inscriptions, ou laisser l'auto-renouvellement créer le suivant.
              </div>
            </div>
          ) : (
            <div className="text-sm text-zinc-500">Chargement…</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoOpen(false)}>Annuler</Button>
            <Button onClick={confirmAutoGenerate} className="bg-purple-500 hover:bg-purple-400 text-white" data-testid="confirm-auto-gen-btn">
              ✅ Créer cet événement (brouillon)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl" data-testid="create-dialog">
          <DialogHeader><DialogTitle>Nouvelle édition — Jeux Martiaux</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jeux Martiaux Inter-Écoles — Institut Impact Élite Martial — Printemps 2026" className="bg-black border-zinc-800" data-testid="form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-black border-zinc-800" data-testid="form-date" />
              </div>
              <div>
                <Label className="text-xs">Lieu</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Val-d'Or" className="bg-black border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs">Adresse complète (affiche + page d'inscription)</Label>
                <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="1218, 7e Rue, Val-d'Or, QC J9P 5S7" className="bg-black border-zinc-800" data-testid="event-address-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Disciplines *</Label>
              <div className="flex gap-2 flex-wrap mt-2">
                {Object.entries(DISCIPLINE_LABEL).filter(([k]) => k !== "kickboxing_educatif").map(([k, lbl]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm((f) => ({
                      ...f,
                      disciplines: f.disciplines.includes(k) ? f.disciplines.filter((d) => d !== k) : [...f.disciplines, k]
                    }))}
                    className={`px-3 py-1 rounded-full text-xs border ${form.disciplines.includes(k) ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-zinc-700 text-zinc-400"}`}
                    data-testid={`form-discipline-${k}`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Capacité max</Label>
                <Input type="number" value={form.capacity_max} onChange={(e) => setForm({ ...form, capacity_max: Number(e.target.value) })} className="bg-black border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs">Frais par discipline (CAD)</Label>
                <Input type="number" value={form.fee_cad_per_discipline} onChange={(e) => setForm({ ...form, fee_cad_per_discipline: Number(e.target.value) })} className="bg-black border-zinc-800" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Décharge de responsabilité</Label>
              <Textarea value={form.waiver_text} onChange={(e) => setForm({ ...form, waiver_text: e.target.value })} className="bg-black border-zinc-800 min-h-[80px]" />
            </div>
            <div>
              <Label className="text-xs">Horaire des plateaux (1 ligne par discipline — s'affiche sur l'affiche)</Label>
              <Textarea value={form.plateaux_schedule} onChange={(e) => setForm({ ...form, plateaux_schedule: e.target.value })} placeholder={"Muay Thaï — 10 h — Plateau A\nJJB — 13 h — Plateau B"} className="bg-black border-zinc-800 min-h-[80px]" data-testid="form-plateaux" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={createEvent} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="form-submit-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {posterEvent && <EventPoster event={posterEvent} onClose={() => setPosterEvent(null)} onUpdated={async (patch) => { setPosterEvent((p) => ({ ...p, ...patch })); await load(); }} />}
    </div>
  );
}

const JM_POSTER_FORMATS = [
  { key: "carre", label: "Post carré (1:1)" },
  { key: "story", label: "Story / Reel (9:16)" },
  { key: "porte", label: "Affiche porte (verticale)" },
];

function EventPoster({ event, onClose, onUpdated }) {
  const origin = window.location.origin;
  const url = event.slug ? `${origin}/competitions/${event.slug}` : `${origin}/cours-essai`;
  const disciplines = (event.disciplines || []).map((d) => DISCIPLINE_LABEL[d] || d).join(" · ");
  const registrationsOpen = event.status === "open";

  const [aiFormat, setAiFormat] = useState("carre");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [aiUrl, setAiUrl] = useState(null);
  const [loc, setLoc] = useState(event.location || "");
  const [addr, setAddr] = useState(event.address || "");
  const [savingLoc, setSavingLoc] = useState(false);
  const locDirty = loc !== (event.location || "") || addr !== (event.address || "");

  const saveLocation = async () => {
    setSavingLoc(true);
    try {
      await api.patch(`/mini-competitions/${event.id}`, { location: loc, address: addr });
      toast.success("Lieu et adresse mis à jour — l'affiche et la page d'inscription sont à jour");
      onUpdated?.({ location: loc, address: addr });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSavingLoc(false);
  };

  const openRegistrations = async () => {
    if (!window.confirm("Ouvrir les inscriptions ? La page du code QR deviendra active et les familles pourront s'inscrire.")) return;
    try {
      await api.patch(`/mini-competitions/${event.id}`, { status: "open" });
      toast.success("✅ Inscriptions ouvertes — le QR mène maintenant à la page active");
      onUpdated?.({ status: "open" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const pollJob = async (jobId) => {
    const deadline = Date.now() + 240000; // 4 min max
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data } = await api.get(`/poster/job/${jobId}`);
      if (data.status === "done") return data.result || {};
      if (data.status === "error") throw new Error(data.error || "Échec de la génération.");
    }
    throw new Error("Délai dépassé — réessaie.");
  };

  const generateAi = async () => {
    setAiLoading(true);
    setAiUrl(null);
    setAiProgress(0);
    toast.info("🎨 Génération de l'affiche visuelle (IA)… ~60-90 s");
    const start = Date.now();
    const timer = setInterval(() => {
      setAiProgress(Math.min(95, Math.round(((Date.now() - start) / 70000) * 100)));
    }, 500);
    try {
      const { data } = await api.post("/poster/jeux-martiaux", {
        format: aiFormat,
        name: event.name,
        date: event.date || "",
        location: event.location || "",
        address: event.address || "",
        disciplines_label: disciplines,
        plateaux_schedule: event.plateaux_schedule || "",
        qr_url: url,
      });
      const result = await pollJob(data.job_id);
      setAiProgress(100);
      setAiUrl(`${BACKEND}${result.url}`);
      toast.success("✅ Affiche visuelle générée !");
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Échec de la génération. Réessaie.");
    } finally {
      clearInterval(timer);
      setAiLoading(false);
    }
  };

  const packBody = () => ({
    name: event.name, date: event.date || "", location: event.location || "", address: event.address || "",
    disciplines_label: disciplines, plateaux_schedule: event.plateaux_schedule || "",
    qr_url: url,
  });

  const downloadAi = async () => {
    if (!aiUrl) return;
    try {
      const resp = await fetch(aiUrl);
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `affiche-jeux-martiaux-${aiFormat}.png`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error("Téléchargement impossible."); }
  };

  const print = () => {
    const node = document.getElementById("jm-poster-print");
    if (!node) return;
    const w = window.open("", "_blank", "width=840,height=1180");
    if (!w) { toast.error("Autorise les fenêtres pop-up pour imprimer l'affiche."); return; }
    w.document.write(`<html><head><title>Affiche — ${event.name}</title><style>@page{size:A4;margin:0}body{margin:0}</style></head><body>${node.outerHTML}</body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) {} }, 400);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="poster-dialog">
        <DialogHeader><DialogTitle>🖼️ Affiche — {event.name}</DialogTitle></DialogHeader>

        {!registrationsOpen && (
          <div className="rounded-xl border border-red-500/50 bg-red-500/10 p-3 flex items-start gap-3" data-testid="poster-status-warning">
            <span className="text-xl">⚠️</span>
            <div className="flex-1 text-sm">
              <div className="font-semibold text-red-200">Inscriptions non ouvertes (statut : {event.status === "draft" ? "brouillon" : event.status})</div>
              <div className="text-xs text-red-200/80 mt-0.5">Le code QR mène à une page inactive tant que l'événement n'est pas ouvert.</div>
              {(event.status === "draft" || event.status === "closed") && (
                <Button size="sm" onClick={openRegistrations} className="mt-2 bg-emerald-600 hover:bg-emerald-500" data-testid="poster-open-registrations-btn">
                  ✅ Ouvrir les inscriptions maintenant
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3" data-testid="poster-location-editor">
          <div className="text-[11px] tracking-[0.25em] text-zinc-400 font-semibold mb-2">📍 LIEU & ADRESSE (sur l'affiche et la page d'inscription)</div>
          <div className="grid gap-2">
            <Input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Lieu (ex. Institut Impact Élite Martial)" className="bg-black border-zinc-800 h-9" data-testid="poster-location-input" />
            <Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Adresse complète (ex. 1218, 7e Rue, Val-d'Or, QC J9P 5S7)" className="bg-black border-zinc-800 h-9" data-testid="poster-address-input" />
            <Button size="sm" onClick={saveLocation} disabled={!locDirty || savingLoc} className="bg-zinc-100 text-black hover:bg-white disabled:opacity-40" data-testid="poster-location-save-btn">
              {savingLoc ? "…" : "💾 Enregistrer le lieu / l'adresse"}
            </Button>
          </div>
        </div>

        {/* ── Affiche visuelle IA (Nano Banana) ── */}
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 mb-1" data-testid="ai-poster-section">
          <div className="text-[11px] tracking-[0.25em] text-amber-400 mb-2 font-semibold">✨ AFFICHE VISUELLE (IA)</div>
          <div className="flex gap-1.5 flex-wrap mb-2">
            {JM_POSTER_FORMATS.map((f) => (
              <button
                key={f.key}
                onClick={() => setAiFormat(f.key)}
                disabled={aiLoading}
                data-testid={`ai-format-${f.key}`}
                className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${aiFormat === f.key ? "border-amber-500 bg-amber-500/20 text-amber-200" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button onClick={generateAi} disabled={aiLoading} className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="ai-generate-btn">
            {aiLoading ? `Génération… ${aiProgress}%` : "✨ Générer une affiche visuelle (IA)"}
          </Button>
          <PosterPackButton
            endpoint="/poster/jeux-martiaux/pack"
            getBody={packBody}
            filename="pack-jeux-martiaux.zip"
            label="📦 Pack 3 formats (aperçu + ZIP)"
            testId="ai-pack-btn"
            discipline="jeux_martiaux"
            disabled={aiLoading}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-md border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 font-semibold px-4 py-2 text-sm disabled:opacity-50"
          />
          {aiLoading && (
            <div className="mt-2 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full bg-amber-500 transition-all" style={{ width: `${aiProgress}%` }} />
            </div>
          )}
          {aiUrl && !aiLoading && (
            <div className="mt-3 space-y-2" data-testid="ai-poster-result">
              <img src={aiUrl} alt="Affiche IA" className="w-full rounded-lg border border-zinc-800" />
              <div className="flex gap-2">
                <Button onClick={downloadAi} size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-500" data-testid="ai-download-btn">⬇️ Télécharger</Button>
                <Button onClick={generateAi} size="sm" variant="outline" className="flex-1" data-testid="ai-regenerate-btn">🔄 Régénérer</Button>
              </div>
            </div>
          )}
        </div>

        <div className="text-[11px] tracking-[0.25em] text-zinc-500 font-semibold mt-1">📐 AFFICHE QR STANDARD (IMPRIMABLE)</div>
        <div className="max-h-[60vh] overflow-auto rounded-lg flex justify-center">
          <div id="jm-poster-print" style={{ width: "420px", background: "linear-gradient(160deg,#0a0a0a,#1c1c1c)", color: "#fff", padding: "32px 28px", fontFamily: "Arial,Helvetica,sans-serif", boxSizing: "border-box" }}>
            <div style={{ color: "#f5b301", fontSize: "11px", letterSpacing: "4px", marginBottom: "10px" }}>RÉSEAU KAIZEN · JEUX MARTIAUX</div>
            <div style={{ fontSize: "30px", fontWeight: 800, lineHeight: 1.1, marginBottom: "8px" }}>{event.name}</div>
            <div style={{ fontSize: "14px", color: "#d4d4d8", marginBottom: "4px" }}>{event.date}{event.location ? ` · ${event.location}` : ""}</div>
            {event.address && <div style={{ fontSize: "12px", color: "#f5b301", marginBottom: "6px" }} data-testid="poster-address">📍 {event.address}</div>}
            {(event.disciplines || []).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "18px" }} data-testid="poster-disciplines">
                {(event.disciplines || []).map((d) => (
                  <span key={d} style={{ fontSize: "11px", color: "#fff", border: "1px solid rgba(245,179,1,0.6)", borderRadius: "999px", padding: "3px 10px" }}>{DISCIPLINE_LABEL[d] || d}</span>
                ))}
              </div>
            )}
            <div style={{ background: "#f5b301", color: "#000", fontWeight: 800, fontSize: "13px", padding: "8px 12px", borderRadius: "8px", display: "inline-block", marginBottom: "20px" }}>🥋 Jeux Martiaux Inter-Écoles</div>
            {(event.plateaux_schedule || "").trim() && (
              <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(245,179,1,0.35)", borderRadius: "10px", padding: "12px 14px", marginBottom: "20px" }}>
                <div style={{ fontSize: "11px", letterSpacing: "2px", color: "#f5b301", marginBottom: "8px" }}>HORAIRE DES PLATEAUX</div>
                {event.plateaux_schedule.split("\n").filter((l) => l.trim()).map((line, i) => (
                  <div key={i} style={{ fontSize: "12px", color: "#e4e4e7", padding: "2px 0" }}>▸ {line.trim()}</div>
                ))}
              </div>
            )}
            <div style={{ background: "#fff", padding: "14px", borderRadius: "12px", width: "fit-content", margin: "0 auto" }}>
              <QRCodeSVG value={url} size={190} level="H" />
            </div>
            <div style={{ textAlign: "center", fontSize: "13px", fontWeight: 700, marginTop: "14px" }}>SCANNE POUR T'INSCRIRE</div>
            <div style={{ textAlign: "center", fontSize: "10px", color: "#71717a", marginTop: "6px", wordBreak: "break-all" }}>{url}</div>
            <div style={{ textAlign: "center", fontSize: "11px", color: "#f5b301", marginTop: "18px", letterSpacing: "2px" }}>INSTITUT IMPACT · VAL-D'OR</div>
            <div style={{ textAlign: "center", fontSize: "11px", color: "#a1a1aa", marginTop: "6px" }}>{PUBLIC_HOST} · 819-527-9446</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button onClick={print} className="bg-sky-500 hover:bg-sky-400 text-black" data-testid="poster-print-btn">🖨️ Imprimer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/50"}`}>
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent ? "text-amber-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function BracketView({ bracket, onRefresh }) {
  const [scoringMatch, setScoringMatch] = useState(null);
  const [winner, setWinner] = useState("a");
  const [method, setMethod] = useState("decision");
  const [critA, setCritA] = useState(EMPTY_CRIT());
  const [critB, setCritB] = useState(EMPTY_CRIT());

  const finalizeMatch = async () => {
    try {
      await api.post(`/mini-competitions/brackets/${bracket.id}/matches/${scoringMatch.id}/finalize`, {
        winner, method, notes: "", criteria_a: critA, criteria_b: critB,
      });
      toast.success(`✅ Résultat + critères enregistrés`);
      setScoringMatch(null);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const rounds = [...new Set(bracket.matches.map((m) => m.round))].sort();
  return (
    <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
      <div className="text-sm font-semibold text-zinc-100 mb-3">{bracket.category_label} · {bracket.total_matches} matches</div>
      <div className="flex gap-4 overflow-x-auto">
        {rounds.map((rnd) => (
          <div key={rnd} className="min-w-[200px] space-y-2">
            <div className="text-[10px] tracking-[0.3em] text-amber-400">RONDE {rnd}</div>
            {bracket.matches.filter((m) => m.round === rnd).map((m) => {
              const aName = m.athlete_a?.athlete_full_name || "BYE";
              const bName = m.athlete_b?.athlete_full_name || "BYE";
              return (
                <button
                  key={m.id}
                  onClick={() => { if (m.athlete_a && m.athlete_b && m.status !== "completed") { setScoringMatch(m); setWinner("a"); setCritA(EMPTY_CRIT()); setCritB(EMPTY_CRIT()); } }}
                  className={`w-full text-left border rounded-lg p-2 text-xs transition ${m.status === "completed" ? "border-emerald-500/30 bg-emerald-500/5" : "border-zinc-800 hover:border-amber-500/40"}`}
                  data-testid={`match-${m.id}`}
                >
                  <div className={`${m.winner === "a" ? "text-emerald-300 font-semibold" : "text-zinc-300"}`}>
                    {aName}
                  </div>
                  <div className="text-zinc-700 text-[10px] my-0.5">vs</div>
                  <div className={`${m.winner === "b" ? "text-emerald-300 font-semibold" : "text-zinc-300"}`}>
                    {bName}
                  </div>
                  {m.rules && (
                    <div className="mt-1 text-[10px] text-zinc-500 whitespace-normal" data-testid={`match-rules-${m.id}`}>
                      <span className="text-amber-300 font-semibold">{m.rules.fight_class || "—"}</span> · {m.rules.round_format}
                      {m.rules.head_strikes && <> · tête : <span className={m.rules.head_strikes === "Interdit" ? "text-red-300" : "text-emerald-300"}>{m.rules.head_strikes}</span></>}
                      {m.rules.forbidden?.length > 0 && <div className="text-red-300/80">Interdit : {m.rules.forbidden.join(", ")}</div>}
                    </div>
                  )}
                  {m.method && <div className="text-[10px] text-amber-400 mt-1">{m.method}</div>}
                  {m.video_url && <a href={m.video_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="block text-[10px] text-red-300 underline mt-1" data-testid={`match-video-${m.id}`}>▶️ Replay</a>}
                </button>
              );
            })}
          </div>
        ))}
        {bracket.matches.length === 0 && (
          <div className="text-xs text-zinc-600 italic">Catégorie à 1 athlète : pointage de participation (regroupement à venir).</div>
        )}
      </div>

      <Dialog open={!!scoringMatch} onOpenChange={(o) => !o && setScoringMatch(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="scoring-dialog">
          <DialogHeader><DialogTitle>Résultat du combat + critères /10</DialogTitle></DialogHeader>
          {scoringMatch && (
            <div className="space-y-4">
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setWinner("a")}
                  className={`w-full p-3 rounded-lg border text-left ${winner === "a" ? "border-amber-500 bg-amber-500/10" : "border-zinc-800"}`}
                  data-testid="winner-a-btn"
                >
                  🅰️ {scoringMatch.athlete_a?.athlete_full_name}
                  <div className="text-[10px] text-zinc-500">{scoringMatch.athlete_a?.school_name}</div>
                </button>
                <button
                  type="button"
                  onClick={() => setWinner("b")}
                  className={`w-full p-3 rounded-lg border text-left ${winner === "b" ? "border-amber-500 bg-amber-500/10" : "border-zinc-800"}`}
                  data-testid="winner-b-btn"
                >
                  🅱️ {scoringMatch.athlete_b?.athlete_full_name}
                  <div className="text-[10px] text-zinc-500">{scoringMatch.athlete_b?.school_name}</div>
                </button>
                <button type="button" onClick={() => setWinner("draw")} data-testid="winner-draw-btn"
                  className={`w-full p-2 rounded-lg border text-center text-xs ${winner === "draw" ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-zinc-800 text-zinc-400"}`}>
                  🤝 Match nul (2 pts chacun)
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3" data-testid="criteria-grid">
                {[["a", critA, setCritA, scoringMatch.athlete_a], ["b", critB, setCritB, scoringMatch.athlete_b]].map(([side, crit, setCrit, ath]) => (
                  <div key={side} className="rounded-lg border border-zinc-800 p-2 space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">Critères — {ath?.athlete_full_name}</div>
                    {CRITERIA.map(([k, lbl]) => (
                      <label key={k} className="flex items-center gap-2 text-[11px] text-zinc-300">
                        <span className="w-24 shrink-0">{lbl}</span>
                        <input type="range" min={0} max={10} step={0.5} value={crit[k]} onChange={(e) => setCrit({ ...crit, [k]: Number(e.target.value) })} className="flex-1 accent-amber-500" data-testid={`crit-${side}-${k}`} />
                        <span className="w-7 text-right font-mono text-amber-300">{crit[k]}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-xs">Méthode</Label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {["decision", "submission", "ko", "rsc", "walkover"].map((md) => (
                    <button
                      key={md}
                      type="button"
                      onClick={() => setMethod(md)}
                      className={`px-3 py-1 rounded-full text-xs border ${method === md ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-zinc-700 text-zinc-400"}`}
                    >
                      {md}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScoringMatch(null)}>Annuler</Button>
            <Button onClick={finalizeMatch} className="bg-emerald-500 hover:bg-emerald-400 text-black" data-testid="finalize-match-btn">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
